import { authFetch } from "@/lib/api-client";
import type { LlmProvider } from "./llm-usage.api";

// ─── Model prices ───────────────────────────────────────────────────
// What each model costs, which is what turns the token counts on the tab into
// money. Kept as its own module rather than folded into `llm-usage.api`: this
// is the only part of the feature that writes, and it is read by the settings
// page rather than by the tab.

/**
 * Rates are micro-dollars (millionths of a dollar) per million tokens, so a
 * published price of $1.25 per million arrives as 1_250_000. Integers all the
 * way from Postgres to here, so no rate picks up float drift in transit; the
 * editor converts to dollars only at the input.
 */
export interface LlmModelPrice {
  provider: LlmProvider;
  model: string;
  inputPerMillion: number;
  outputPerMillion: number;
  thinkingPerMillion: number;
  cacheWritePerMillion: number;
  cacheReadPerMillion: number;
  updatedByName: string | null;
  updatedAt: string;
  /** Seeded from the list price Nexus ships, and not saved by an admin since. */
  isListPrice: boolean;
  /** Nexus ships a list price for this model, so the row can be reset to it. */
  hasListPrice: boolean;
}

/** A model seen recently with no rate set, and what it has spent unpriced. */
export interface UnpricedModel {
  provider: LlmProvider;
  model: string;
  totalTokens: number;
}

export interface LlmModelPrices {
  prices: LlmModelPrice[];
  unpriced: UnpricedModel[];
  /** Catalog models with no rate and no recent calls, listed so they can be priced ahead. */
  unpricedCatalog: { provider: LlmProvider; model: string }[];
}

/** The rates a save sends. The credit and source fields are server-owned. */
export type LlmModelPriceInput = Omit<
  LlmModelPrice,
  "updatedByName" | "updatedAt" | "isListPrice" | "hasListPrice"
>;

/** An API from before `unpricedCatalog` omits it, which a rolling deploy can still serve. */
type LlmModelPricesResponse = Omit<LlmModelPrices, "unpricedCatalog"> &
  Partial<Pick<LlmModelPrices, "unpricedCatalog">>;

export async function fetchLlmModelPrices(): Promise<LlmModelPrices> {
  const { data } = await authFetch<LlmModelPricesResponse>("/admin/llm-usage/prices");
  return { ...data, unpricedCatalog: data.unpricedCatalog ?? [] };
}

export async function saveLlmModelPrice(input: LlmModelPriceInput): Promise<LlmModelPrice> {
  const { data } = await authFetch<LlmModelPrice>("/admin/llm-usage/prices", {
    method: "PUT",
    body: JSON.stringify(input),
  });
  return data;
}

export async function resetLlmModelPrice(
  provider: LlmProvider,
  model: string,
): Promise<LlmModelPrice> {
  const { data } = await authFetch<LlmModelPrice>(
    `/admin/llm-usage/prices/${provider}/${encodeURIComponent(model)}/reset`,
    { method: "POST" },
  );
  return data;
}

export async function deleteLlmModelPrice(
  provider: LlmProvider,
  model: string,
): Promise<void> {
  await authFetch(`/admin/llm-usage/prices/${provider}/${encodeURIComponent(model)}`, {
    method: "DELETE",
  });
}
